import * as Sentry from '@sentry/react';
import { RTCClientInitialized } from 'features/application/actions';
import { selectAppReconnecting, selectDeviceType } from 'features/application/applicationSlice';
import { DevMode } from 'features/dev-mode/DevMode';
import { streamMinimized } from 'features/layout/actions';
import { selectMaximizedStream } from 'features/layout/features/content/contentSlice';
import { screenshareStarted } from 'features/screenshare/actions';
import { generalDataUpdated } from 'features/streaming/streamingSlice';

import { UserId } from 'features/users/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import Janus from 'lib/janus';
import { SignalingRoomUser } from 'services/signaling';
import { store } from 'store/store';
import { eventBus } from 'utils/eventBus';
import { formatErrorForLogging, logger } from 'utils/logger';
import { userAgentDetails } from 'utils/userAgentDetails';
import { NO_ROOM_ID } from 'utils/webrtc/errors';
import { RTCClient } from 'utils/webrtc/index';
import { StatsOrchestrator } from 'utils/webrtc/stats/StatsOrchestrator';
import { MediaServerConnector } from 'utils/webrtc/MediaServerConnector';
import { PublishingFeed } from 'utils/webrtc/publishing/PublishingFeed';
import { ScreensharingFeed } from 'utils/webrtc/publishing/ScreensharingFeed';
import { ReceivingFeed } from 'utils/webrtc/ReceivingFeed';
import {
  BroadcastingByUserId,
  ConnectionMediaTokens,
  FeedStreamInfo,
  JanusConnection,
  JanusConnectionType,
  PublishingKind,
  PublishingOptions,
  PublishingRoomJoinedMessage,
} from 'utils/webrtc/types';
import webrtcAdapter from 'webrtc-adapter';
import {
  FeedId,
  GeneralStreamingUIState,
  MediaStreamType,
  StreamingStarted,
  SubscribeStreamList,
} from 'features/streaming/types';

import { selectBreakoutRoomReconnecting } from 'features/breakout-rooms/breakoutRoomsSlice';
import { VirtualBackgroundManager } from 'utils/webrtc/virtual-background/VirtualBackgroundManager';
import { calculateVideoFeeds, filterSubscriptionsWithVideo, orderSubscriptions } from './utils';

export class Client {
  roomJoined: boolean = false;

  roomId?: string;

  roomPin?: string;

  breakoutRoomId?: string;

  mountpointId?: string;

  isMediaServerError: boolean = false;

  supressErrors: boolean = false;

  connections: Record<string, JanusConnection> = {};

  mediaServerConnector: MediaServerConnector = new MediaServerConnector();

  publishingFeed: PublishingFeed = new PublishingFeed();

  screensharingFeed: ScreensharingFeed = new ScreensharingFeed();

  receivingFeed: ReceivingFeed = new ReceivingFeed();

  localFeeds: Record<string, boolean> = {};

  privateId?: number;

  mediaDebug: DevMode = new DevMode();

  userIdByFeedId: Record<FeedId, UserId> = {};

  userIdByScreensharingFeedId: Record<FeedId, UserId> = {};

  broadcastingByUserId: BroadcastingByUserId = {};

  screensharingByUserId: BroadcastingByUserId = {};

  publishOptions: PublishingOptions = {};

  initialSubscriptions?: SignalingRoomUser[];

  initialScreenshareSubscription?: {
    id: UserId;
    feedId: FeedId;
    streams: FeedStreamInfo[];
  };

  isUnloading: boolean = false;

  statsOrchestrator?: StatsOrchestrator;

  receiveMode: 'videoroom' | 'streaming' = 'videoroom';

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor() {
    /*
    It's a singleton class. Most of the stuff you would want to put in here could
    be safely made static or be called sometime later at execution time
    */

    const deviceInfo = userAgentDetails();
    const eventName = deviceInfo.os.name === 'iOS' ? 'pagehide' : 'unload';
    window.addEventListener(eventName, () => {
      this.isUnloading = true;
    });

    // Continuous visibility timeline for the audio-stuck telemetry — event-driven logs
    // can't otherwise bound when the page left the foreground.
    document.addEventListener('visibilitychange', () => {
      logger
        .remote({ system: true, capture: 'streaming' })
        .info('[VISIBILITY] visibility changed', {
          hidden: document.hidden,
        });
    });
  }

  private get uiState(): GeneralStreamingUIState {
    return {
      userIdByFeedId: { ...this.userIdByFeedId },
      userIdByScreensharingFeedId: { ...this.userIdByScreensharingFeedId },
      broadcastingByUserId: { ...this.broadcastingByUserId },
      screensharingByUserId: { ...this.screensharingByUserId },
    };
  }

  subscribeToServer = (connection: JanusConnection) => {
    this.receivingFeed.attachToConnection(connection);
  };

  initialize = (roomId: string, roomPin: string, mountpointId?: string) => {
    this.roomId = roomId;
    this.roomPin = roomPin;
    this.mountpointId = mountpointId;

    Janus.init({
      debug: process.env.REACT_APP_JANUS_DEBUG === 'true',
      // eslint-disable-next-line react-hooks/rules-of-hooks
      dependencies: Janus.useDefaultDependencies({ adapter: webrtcAdapter }),
      console: logger,
      callback: () => {
        store.dispatch(RTCClientInitialized());
      },
    });
  };

  startStats = () => {
    if (this.statsOrchestrator) {
      return;
    }
    this.statsOrchestrator = new StatsOrchestrator({ rtcClient: this });
    this.statsOrchestrator.start();
  };

  destroy = () => {
    logger.log('Destroying WebRTC client');

    this.statsOrchestrator?.stop();
    this.statsOrchestrator = undefined;

    // Suppress media-failure modals from any in-flight ICE recovery
    // (e.g. Janus hangup arriving mid-restart) — this is an intentional
    // teardown, not a network failure. Cleared in establishJanusConnectionSaga.
    this.supressErrors = true;

    // this needs to be a copy of connection list because otherwise
    // lookup from recursive calls fails
    const connections = { ...this.mediaServerConnector.connections };

    Object.keys(connections).forEach((handle) => {
      const connection = connections[handle];

      if (!connection.replicatedHandle) {
        // Reconnect must evict synchronously: a dead transport skips janus's
        // async `destroyed` callback, so a stale entry gets reattached → 458.
        if (this.isMediaServerError || selectAppReconnecting(store.getState())) {
          this.mediaServerConnector.cleanupConnection(handle);
        } else {
          connection.janus.destroy();
        }
      }
    });

    this.publishingFeed.cleanupJoinMedia();
    this.publishingFeed.markIdle();
    this.receivingFeed.subscribedScreenshares = {};

    this.broadcastingByUserId = {};
    this.userIdByFeedId = {};
    this.privateId = undefined;
    this.localFeeds = {};
    this.roomJoined = false;

    this.mediaServerConnector.subscriptionQueue = [];
    this.mediaServerConnector.pendingServerConnections = [];

    this.updateRedux();
  };

  // reset to initial state to allow for new connections;
  reset = (preserveMediaStates = false) => {
    logger.remote({ tier: 1 }).log('Resetting WebRTC client');

    // Keep the orchestrator alive across breakout-room movement so polling
    // resumes on the new peer connections; only clear room-scoped caches.
    this.statsOrchestrator?.resetForRoomChange();

    this.supressErrors = true;

    const { preserveVideo, preserveAudio } =
      this.publishingFeed.getResetMediaStates(preserveMediaStates);

    this.publishingFeed.cleanupConnection();
    // Clear streams from Redux state to prevent race condition where old feed instances
    // update Redux with stale stream IDs after new feed instances are created
    this.publishingFeed.cleanupUIStreams();
    // @TODO we might extend this pattern to other feeds as they might suffer from the same issue with cleanup->re-init raise conditions
    // reference: feed-disposal-race-condition.md
    // Dispose publishing feed to prevent race conditions from old cleanup callbacks
    this.publishingFeed.dispose();
    VirtualBackgroundManager.reset();
    this.publishingFeed = new PublishingFeed();

    this.screensharingFeed.cleanupConnection();
    this.screensharingFeed.dispose();

    this.receivingFeed.cleanupConnection(true);
    // CRITICAL: Clean up audio elements synchronously before creating new instance.
    // The saga-based cleanup won't work because RTCClient.receivingFeed will already
    // point to the new instance by the time the async cleanup callback runs.
    this.receivingFeed.cleanupFeed();

    // this needs to be a copy of connection list because otherwise
    // lookup from recursive calls fails
    const connections = { ...this.mediaServerConnector.connections };

    Object.keys(connections).forEach((handle) => {
      this.mediaServerConnector.cleanupConnection(handle);
    });

    this.publishingFeed.restorePreservedMediaStates({ preserveVideo, preserveAudio });

    const screensharingState = this.screensharingFeed.captureScreensharingState();

    this.screensharingFeed = new ScreensharingFeed();
    this.receivingFeed = new ReceivingFeed();

    if (screensharingState) {
      // @TODO In the future, it might be possible to refactor this to use a global app isReconnecting.
      const isReconnecting = selectBreakoutRoomReconnecting(store.getState());

      if (isReconnecting) {
        this.screensharingFeed.restoreScreensharingState(screensharingState);
      }
    }

    this.broadcastingByUserId = {};
    this.userIdByFeedId = {};
    this.privateId = undefined;
    this.localFeeds = {};
    this.roomJoined = false;

    this.mediaServerConnector.subscriptionQueue = [];
    this.mediaServerConnector.pendingServerConnections = [];

    this.updateRedux();
  };

  setupPublishState = (options: PublishingOptions = {}, broadcastIntended: boolean = false) => {
    this.publishingFeed.markBroadcastIntended(broadcastIntended);

    this.screensharingFeed.scheduleReconnectPublish();
    this.receivingFeed.resetSimulcastSettings();

    this.publishOptions = options;

    if (broadcastIntended) {
      this.publishingFeed.configureStreamingState(options);
    } else {
      this.publishingFeed.resetMediaStates();
      this.publishingFeed.updateRedux();
    }

    this.publishingFeed.cleanupJoinMedia();
  };

  resetPublishOptions = () => {
    this.publishOptions = {};
  };

  setInitialSubscriptions = (users: SignalingRoomUser[]) => {
    this.initialSubscriptions = users;

    const broadcastingStateByUserId: BroadcastingByUserId = { ...this.broadcastingByUserId };

    let latestTimestamp: number = 0;

    users.forEach((user) => {
      if (user.feedId) {
        this.userIdByFeedId[user.feedId] = user.id;
      }

      if (!broadcastingStateByUserId[user.id]) {
        broadcastingStateByUserId[user.id] = {
          audio: user.audioEnabled,
          video: user.videoEnabled,
        };
      } else {
        const existingState = broadcastingStateByUserId[user.id];
        // Prefer existing runtime state; only fill when undefined to avoid re-enabling after stop.
        broadcastingStateByUserId[user.id] = {
          audio: existingState.audio ?? user.audioEnabled,
          video: existingState.video ?? user.videoEnabled,
        };

        if (existingState.audio || existingState.video) {
          logger
            .remote({ system: true, capture: 'streaming' })
            .debug('Merging existing broadcasting state with room joined data', {
              userId: user.id,
              existingState,
              roomJoinedState: { audio: user.audioEnabled, video: user.videoEnabled },
              finalState: broadcastingStateByUserId[user.id],
            });
        }
      }

      // Allow for only a single screenshare at the moment;
      if (user.screensharing) {
        const timestamp = user.pushedScreenshareTimestamp || user.startScreenshareTimestamp;
        if (
          user.screenshareFeedId &&
          user.screenshareStreams &&
          timestamp &&
          timestamp > latestTimestamp
        ) {
          latestTimestamp = timestamp;
          this.initialScreenshareSubscription = {
            id: user.id,
            feedId: user.screenshareFeedId,
            streams: user.screenshareStreams,
          };
        }
      }
    });

    this.broadcastingByUserId = broadcastingStateByUserId;

    this.updateRedux();
  };

  attemptPublish = () => {
    if (!this.roomId) {
      throw NO_ROOM_ID();
    }

    if (RTCClient.publishingFeed.broadcastIntended) {
      this.publishingFeed.attachPlugin();
    }

    if (this.screensharingFeed.pendingReconnect) {
      this.screensharingFeed.shareScreen();
    }
    // else {
    //   // TODO: Pay attention to these cleanups;
    //   this.screensharingFeed.cleanupConnection();
    // }
  };

  subscribe = (
    { feedId, streams, id }: StreamingStarted,
    kind: 'stream' | 'screenshare' | 'external' | 'phone' = 'stream'
  ) => {
    if (!this.roomId) {
      throw NO_ROOM_ID();
    }

    if (this.localFeeds[feedId]) {
      return;
    }

    const subscribeStreams: SubscribeStreamList = [];

    streams.forEach((stream) => {
      subscribeStreams.push({ feed: feedId, mid: stream.mid, options: stream.options });
    });

    if (kind === 'screenshare') {
      this.userIdByScreensharingFeedId[feedId] = id;
    } else if (kind === 'stream') {
      this.userIdByFeedId[feedId] = id;
    }

    this.updateRedux();

    this.subscribeConnections(subscribeStreams);
  };

  subscribeToRoomUsers = () => {
    if (!this.roomId) {
      throw NO_ROOM_ID();
    }

    // Screenshare subscription is handled by sagas (not by videoroom subscribe),
    // so dispatch it before the streaming early return
    if (this.initialScreenshareSubscription) {
      store.dispatch(screenshareStarted(this.initialScreenshareSubscription));
      this.initialScreenshareSubscription = undefined;
    }

    if (this.receiveMode === 'streaming') {
      return;
    }

    const streams = this.prepareInitialSubscriptions();

    this.subscribeConnections(streams);

    this.initialSubscriptions = undefined;
  };

  subscribeConnections = (streams: SubscribeStreamList) => {
    if (!streams.length) {
      return;
    }

    const connections = Object.values(this.mediaServerConnector.connections).filter(
      (connection) => connection.type === JanusConnectionType.subscription
    );

    if (connections.length) {
      connections.forEach((connection) => {
        this.receivingFeed.requestSubscription(connection.handle, streams).catch((error) => {
          if (this.supressErrors) {
            return;
          }

          const message = '[Client] Subscription request failed, the streams were dropped';
          const context = {
            connectionHandle: connection.handle,
            streams: streams.map(({ feed: feedId, mid }) => ({ feedId, mid })),
          };

          logger.remote({ system: true, capture: 'streaming' }).error(message, context);

          Sentry.captureException(new Error(message, { cause: error }), {
            tags: { component: 'Client', operation: 'subscribe-request' },
            contexts: { subscriptionRequest: { ...context, ...formatErrorForLogging(error) } },
          });
        });
      });
    } else {
      this.mediaServerConnector.queueStreamSubscription(streams);
    }
  };

  disconnectFeed = (feedId: FeedId) => {
    const userId = this.userIdByFeedId[feedId] || this.userIdByScreensharingFeedId[feedId];

    const maximizedStream = selectMaximizedStream(store.getState());
    if (maximizedStream?.userId === userId) {
      store.dispatch(streamMinimized());
    }

    delete this.userIdByFeedId[feedId];

    this.updateRedux();
  };

  setupLocalFeed = (data: PublishingRoomJoinedMessage) => {
    const userId = selectLocalUserId(store.getState());

    this.localFeeds[data.id] = true;
    this.privateId = data.private_id;
    this.userIdByFeedId[data.id] = userId;

    this.updateRedux();
  };

  setBroadcastingState = (userId: UserId, streams: FeedStreamInfo[]) => {
    this.broadcastingByUserId[userId] ??= {};

    streams.forEach((stream) => {
      eventBus.sendMessage(`${stream.type}Enabled`, { userId });

      this.broadcastingByUserId[userId] = {
        ...this.broadcastingByUserId[userId],
        [stream.type]: true,
      };
    });

    this.updateRedux();
  };

  removeBroadcastingDevice = (userId: UserId, type: MediaStreamType) => {
    this.broadcastingByUserId[userId] ??= {};

    eventBus.sendMessage(`${type}Disabled`, { userId });

    this.broadcastingByUserId[userId] = {
      ...this.broadcastingByUserId[userId],
      [type]: false,
    };

    this.updateRedux();
  };

  enableScreensharingMedia = (userId: UserId, streams: FeedStreamInfo[]) => {
    this.screensharingByUserId[userId] ??= {};

    streams.forEach((stream) => {
      this.screensharingByUserId[userId] = {
        ...this.screensharingByUserId[userId],
        [stream.type]: true,
      };
    });

    this.updateRedux();
  };

  disableScreensharingMedia = (userId: UserId, type: MediaStreamType) => {
    if (this.screensharingByUserId[userId]) {
      this.screensharingByUserId[userId] = {
        ...this.screensharingByUserId[userId],
        [type]: false,
      };

      this.updateRedux();
    }
  };

  getPublishingFeed = (kind: PublishingKind) => this[`${kind}Feed`];

  getJanusPlugin = () => this.receivingFeed.receiver?.plugin?.janusPlugin;

  isMobileScreenshareFeedId = (feedId: string) =>
    this.localFeeds[feedId] && this.screensharingFeed.mobileScreenshareEnabled;

  // todo: move to ms connector
  refreshConnectionMediaTokens = (tokens: ConnectionMediaTokens) => {
    Object.values(this.mediaServerConnector.connections).forEach((connection) => {
      if (!connection.replicatedHandle) {
        if (connection.type === JanusConnectionType.publishing && tokens.publishing) {
          connection.janus.updateAuthToken(tokens.publishing);
        }

        if (connection.type === JanusConnectionType.subscription && tokens.subscription) {
          connection.janus.updateAuthToken(tokens.subscription);
        }
      }
    });
  };

  setBreakoutRoomId = (roomId: string) => {
    this.breakoutRoomId = roomId;
  };

  private updateRedux = () => {
    const state = this.uiState;
    store.dispatch(generalDataUpdated(state));
  };

  private prepareInitialSubscriptions = () => {
    const streams: SubscribeStreamList = [];

    const deviceType = selectDeviceType(store.getState());

    const videoLimit = deviceType === 'mobile' ? 4 : 49;

    let videoTiles = 0;

    const subscriptionsWithVideoStream = filterSubscriptionsWithVideo(this.initialSubscriptions);
    const feedsWithVideo = calculateVideoFeeds(subscriptionsWithVideoStream);
    const orderedVideoSubscriptions = orderSubscriptions(subscriptionsWithVideoStream);

    const bands = this.receivingFeed.evaluateQualityBands(feedsWithVideo);
    const defaultConfig = this.receivingFeed.getBaseSimulcastConfig(bands);
    const speakerConfig = this.receivingFeed.getSpeakerSimulcastConfig(bands);

    const specialConfig: Record<UserId, number[]> = {};

    if (bands.crowded) {
      orderedVideoSubscriptions.slice(0, 2).forEach((user, idx) => {
        specialConfig[user.id] = speakerConfig[idx];
      });
    }

    this.initialSubscriptions?.forEach((user) => {
      if (!user.feedId) {
        logger
          .remote({ system: true, capture: 'streaming' })
          .debug(
            'Skipping initial subscription for user without feedId — will subscribe via streamStarted',
            { userId: user.id, streams: user.streams }
          );
        return;
      }

      user.streams?.forEach((stream) => {
        const { feedId } = user;

        const { mid, type } = stream;

        if (stream.type === 'video') {
          if (videoTiles < videoLimit) {
            const [substream, temporal] = specialConfig[user.id]
              ? specialConfig[user.id]
              : defaultConfig;

            streams.push({
              feed: feedId,
              mid,
              options: { substream, temporal },
            });
            videoTiles += 1;
          }
        } else {
          streams.push({ feed: feedId, mid, options: { substream: 0, temporal: 0 } });
        }

        this.receivingFeed.media.setKind(feedId, mid, type);
      });
    });

    return streams;
  };
}

export default Client;
